import React, { useState, useEffect } from 'react';
import './CustomizableDesignNotice.css';

const CustomizableDesignNotice = ({
  variant = 'tooltip',
  position = 'top',
  trigger = 'hover',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenSeen, setHasBeenSeen] = useState(false);

  useEffect(() => {
    // Auto-show banner variant on first visit
    if (variant === 'banner' && !hasBeenSeen) {
      const hasSeen = localStorage.getItem('customizableDesignNoticeSeen');
      if (!hasSeen) {
        setIsVisible(true);
        setTimeout(() => {
          setIsVisible(false);
          localStorage.setItem('customizableDesignNoticeSeen', 'true');
          setHasBeenSeen(true);
        }, 8000); // Show for 8 seconds
      }
    }
  }, [variant, hasBeenSeen]);

  const messages = {
    short: 'Designs are 100% customizable!',
    medium:
      'Our artists can customize any design! Need to change the text or alter the design? Just let us know in the design notes.',
    long:
      'Our talented art team can customize any design to match your vision perfectly. Change colors, add text, modify graphics, combine elements - the possibilities are endless!',
    cta: 'Want something changed? Just let us know in the design notes!',
  };

  const renderTooltip = () => (
    <div className="customizable-notice-wrapper">
      <div
        className="customizable-notice-trigger"
        role="button"
        tabIndex={0}
        onMouseEnter={() => trigger === 'hover' && setIsVisible(true)}
        onMouseLeave={() => trigger === 'hover' && setIsVisible(false)}
        onClick={() => trigger === 'click' && setIsVisible(!isVisible)}
        onKeyDown={e =>
          e.key === 'Enter' && trigger === 'click' && setIsVisible(!isVisible)
        }
      >
        <span className="magic-wand-icon" role="img" aria-label="sparkles">
          ✨
        </span>
        <span className="customize-text">Fully Customizable</span>
      </div>
      {isVisible && (
        <div className={`customizable-tooltip ${position}`}>
          <div className="tooltip-content">
            <div className="tooltip-header">
              <span
                className="paint-icon"
                role="img"
                aria-label="artist palette"
              >
                🎨
              </span>
              <strong>Make it Yours!</strong>
            </div>
            <p>{messages.medium}</p>
            <p className="tooltip-cta">{messages.cta}</p>
          </div>
          <div className="tooltip-arrow"></div>
        </div>
      )}
    </div>
  );

  const renderBanner = () => (
    <>
      {isVisible && (
        <div
          className="banner-overlay"
          role="button"
          tabIndex={0}
          onClick={() => setIsVisible(false)}
          onKeyDown={e => e.key === 'Enter' && setIsVisible(false)}
          aria-label="Close overlay"
        />
      )}
      <div
        className={`customizable-banner ${isVisible ? 'visible' : 'hidden'}`}
      >
        <div className="banner-content">
          <div className="banner-icon-group">
            <span
              className="banner-icon"
              role="img"
              aria-label="artist palette"
            >
              🎨
            </span>
            <span className="banner-icon" role="img" aria-label="pencil">
              ✏️
            </span>
            <span className="banner-icon" role="img" aria-label="target">
              🎯
            </span>
          </div>
          <div className="banner-text">
            <h3>Did you know? Every design is fully customizable!</h3>
            <p>{messages.long}</p>
          </div>
          <button
            className="banner-close"
            onClick={() => setIsVisible(false)}
            aria-label="Close notification"
          >
            ×
          </button>
        </div>
      </div>
    </>
  );

  const renderCustomizationMsg = () => (
    <div>
      <small>{messages.long}</small>
    </div>
  );

  const renderInlineNotice = () => (
    <div className="customizable-inline-notice">
      <div className="inline-notice-icon">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 2L13.09 8.26L19 7L15.45 11.82L21 16L14.81 16.95L16 23L11 18L6 23L7.19 16.95L1 16L6.55 11.82L3 7L8.91 8.26L12 2Z"
            fill="#FFD700"
          />
        </svg>
      </div>
      <div className="inline-notice-content">
        <strong>Pro tip:</strong> {messages.medium}
      </div>
    </div>
  );

  switch (variant) {
    case 'tooltip':
      return renderTooltip();
    case 'banner':
      return renderBanner();
    case 'badge':
      return renderCustomizationMsg();
    case 'inline':
      return renderInlineNotice();
    default:
      return renderTooltip();
  }
};

export default CustomizableDesignNotice;
